export const URLS = {
  CONTACT: "https://skygrid.co.jp/contact/",
} as const;

type CurriculumIconKey = "book" | "users" | "target";

export type CurriculumItem = {
  session: string;
  title: string;
  description: string;
  note?: string;
  structure: string;
  duration: string;
  icon: CurriculumIconKey;
};

export type PlatformCurriculum = {
  platform: string;
  items: CurriculumItem[];
  totalHours: string;
};

// セッション数の決定（環境変数 VITE_SESSION_COUNT で切替）
type SessionCount = "3" | "4" | "5";
const SESSION_COUNT: SessionCount =
  (import.meta.env.VITE_SESSION_COUNT as SessionCount) ?? "5";

// ページ全体の定数
export const TRAINING_NAME = "SNS運用研修";
export const HERO_DESCRIPTION =
  "厚生労働省の「人材開発支援助成金（リスキリング支援コース）」を活用し、低コストで効果的な社員のSNS運用スキル向上を実現します。";
export const CURRICULUM_LEAD =
  "各SNSプラットフォームの基礎から実践まで、段階的に運用スキルを習得できる体系的なカリキュラム";
export const PROGRAM_OVERVIEW =
  "SNS運用スキルの内製化を目指す、実践的な人材育成プログラム";
export const BACKGROUND_LEAD =
  "SNSが企業の情報発信・ブランディング・採用・販促の主戦場となる中、各プラットフォームの運用を内製化し、継続的に成果を出す体制づくりが求められています。";

const SESSION_CONFIG: Record<SessionCount, { totalSessions: string; totalHours: string; flowDescription: string; completionTimeline: string }> = {
  "3": {
    totalSessions: "全3回",
    totalHours: "10時間",
    flowDescription: "座学＋ワークショップ形式で、全3回・10時間の実践的な研修を提供します。",
    completionTimeline: "全3回完了",
  },
  "4": {
    totalSessions: "全4回",
    totalHours: "10時間",
    flowDescription: "座学＋ワークショップ形式で、全4回・10時間の実践的な研修を提供します。",
    completionTimeline: "全4回完了",
  },
  "5": {
    totalSessions: "全5回",
    totalHours: "10時間",
    flowDescription: "座学＋ワークショップ形式で、全5回・10時間の実践的な研修を提供します。",
    completionTimeline: "全5回完了",
  },
};

const currentConfig = SESSION_CONFIG[SESSION_COUNT];

export const PROGRAM_STATS = {
  MAX_SUBSIDY_RATE: "75%",
  MIN_COST_PER_PERSON: "10万円〜",
  TOTAL_HOURS: currentConfig.totalHours,
  TOTAL_SESSIONS: currentConfig.totalSessions,
} as const;

export const FLOW_EXECUTION_DESCRIPTION = currentConfig.flowDescription;
export const APPLICATION_COMPLETION_TIMELINE = currentConfig.completionTimeline;

// --- カリキュラムデータ（セッション数別） ---

// 5回コース
const FIVE_SESSION_CURRICULA: PlatformCurriculum[] = [
  {
    platform: "Instagram",
    items: [
      {
        session: "第1回【2時間】",
        title: "Instagram基礎とプロフィール設計",
        description:
          "SNSマーケティングの基本（15分）、Instagramの役割（15分）、プロフィールの作成とカスタマイズ（90分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "book",
      },
      {
        session: "第2回【2時間】",
        title: "クリエイティブ制作とアカウント設計",
        description:
          "Canvaを使って画像を作成（90分）、アカウントの設計を考える（30分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第3回【2時間】",
        title: "アルゴリズムとストーリーズ実践",
        description:
          "アルゴリズムの理解（10分）、インサイト分析（10分）、ガイドライン（10分）、ストーリー作成（90分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "users",
      },
      {
        session: "第4回【2時間】",
        title: "フィード投稿とテーマ設計",
        description:
          "ハイライト作成（40分）、投稿テーマ設計（20分）、フィード投稿（オリジナル）作成（60分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第5回【2時間】",
        title: "投稿運用とリール制作",
        description:
          "投稿とキャプション・ハッシュタグ設計（40分）、リール動画作成（60分）、成功アカウント事例（10分）、未来のInstagramとトレンド（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "TikTok",
    items: [
      {
        session: "第1回【2時間】",
        title: "TikTokの基本と初級クリエイティブ",
        description:
          "TikTokの基本（10分）、プロフィール作成とカスタマイズ（30分）、動画の作成（初級編）（60分）、コンテンツの配信（10分）、コミュニティとのエンゲージメント・分析（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "book",
      },
      {
        session: "第2回【2時間】",
        title: "広告・マネタイズと中級動画制作",
        description:
          "広告とマネタイズ（10分）、ガイドライン（10分）、ケーススタディ（10分）、動画作成（中級編）（90分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第3回【2時間】",
        title: "上級動画制作とベンチマーク",
        description:
          "動画作成（上級編）（90分）、00を真似てみよう！（30分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
      {
        session: "第4回【2時間】",
        title: "オリジナル制作ワーク①",
        description:
          "00を真似てみよう！（60分）、オリジナルを作ってみよう！（60分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第5回【2時間】",
        title: "オリジナル制作ワーク②とトレンド",
        description:
          "オリジナルを作ってみよう！（50分）、完全オリジナルを作ってみよう！（60分）、未来のTikTokとトレンド（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "X（旧Twitter）",
    items: [
      {
        session: "第1回【2時間】",
        title: "Xの基本とアカウント設計",
        description:
          "Xプラットフォームの特徴と最新動向（15分）、ビジネスアカウントの作成とプロフィール設計（45分）、基本的な投稿作成とフォーマット（40分）、タイムラインとアルゴリズムの理解（20分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "book",
      },
      {
        session: "第2回【2時間】",
        title: "コンテンツ戦略とエンゲージメント",
        description:
          "効果的な投稿文の書き方（30分）、ハッシュタグ・メンション活用術（20分）、画像・動画付き投稿の作成（40分）、エンゲージメントを高めるテクニック（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第3回【2時間】",
        title: "スレッド・スペースと分析",
        description:
          "スレッド（長文投稿）の活用（40分）、Xスペース（音声配信）の運用（30分）、Xアナリティクスの見方と改善施策（40分）、ガイドラインと炎上対策（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "users",
      },
      {
        session: "第4回【2時間】",
        title: "広告運用と投稿カレンダー設計",
        description:
          "X広告の種類と出稿方法（30分）、ターゲティングと予算設定（30分）、投稿カレンダーの設計と運用計画（40分）、オリジナル投稿の作成ワーク（20分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第5回【2時間】",
        title: "実践ワークとトレンド活用",
        description:
          "オリジナルコンテンツの企画・投稿（60分）、キャンペーン企画の立案（30分）、成功アカウント事例分析（20分）、最新トレンドと今後のX活用（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "LINE公式アカウント",
    items: [
      {
        session: "第1回【2時間】",
        title: "LINE公式アカウントの基本と開設",
        description:
          "LINE公式アカウントの概要と活用メリット（15分）、アカウント開設とプロフィール設定（45分）、管理画面の基本操作（30分）、あいさつメッセージ・応答メッセージの設定（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "book",
      },
      {
        session: "第2回【2時間】",
        title: "メッセージ配信とリッチメニュー",
        description:
          "メッセージの種類と配信方法（20分）、リッチメッセージ・カードタイプメッセージの作成（50分）、リッチメニューの設計と設定（50分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第3回【2時間】",
        title: "セグメント配信とステップ配信",
        description:
          "オーディエンスとセグメント配信（30分）、ステップ配信の設計と設定（50分）、クーポン・ショップカードの活用（40分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "users",
      },
      {
        session: "第4回【2時間】",
        title: "分析・広告と友だち獲得施策",
        description:
          "LINE公式アカウントの分析機能（30分）、LINE広告の基本と出稿方法（30分）、友だち獲得施策の設計（30分）、実践ワーク：配信計画の作成（30分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第5回【2時間】",
        title: "総合実践と運用計画策定",
        description:
          "オリジナルリッチメニュー・メッセージの作成（50分）、ステップ配信シナリオの設計ワーク（40分）、成功事例の分析（20分）、今後の運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "YouTube",
    items: [
      {
        session: "第1回【2時間】",
        title: "YouTubeの基本とチャンネル設計",
        description:
          "YouTube活用の最新動向と企業活用事例（15分）、チャンネル開設とブランディング設定（45分）、動画コンテンツの企画方法（40分）、YouTube SEOの基本（20分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "book",
      },
      {
        session: "第2回【2時間】",
        title: "撮影基礎とスマホ動画制作",
        description:
          "撮影の基本テクニック（構図・照明・音声）（30分）、スマートフォンでの撮影実践（50分）、動画素材の整理と管理（20分）、著作権・ガイドラインの理解（20分）",
        note: "",
        structure: "座学＋実習",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第3回【2時間】",
        title: "動画編集とサムネイル制作",
        description:
          "動画編集ツールの基本操作（40分）、カット編集・テロップ・BGM挿入（50分）、サムネイル制作の基本とデザイン実践（30分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
      {
        session: "第4回【2時間】",
        title: "YouTubeショートと分析・改善",
        description:
          "YouTubeショートの企画と制作（50分）、YouTube Studioの分析機能（30分）、視聴データの読み方と改善施策（30分）、収益化の仕組みと条件（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "target",
      },
      {
        session: "第5回【2時間】",
        title: "オリジナル動画制作と運用計画",
        description:
          "オリジナル動画の企画・撮影・編集（70分）、コンテンツカレンダーの設計（20分）、成功チャンネル事例分析（20分）、今後のYouTube活用とトレンド（10分）",
        note: "",
        structure: "実習中心",
        duration: "2時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
];

// 3回コース（各プラットフォーム 10時間 = 4h + 3h + 3h）
const THREE_SESSION_CURRICULA: PlatformCurriculum[] = [
  {
    platform: "Instagram",
    items: [
      {
        session: "第1回【4時間】",
        title: "Instagram基礎・プロフィール設計とクリエイティブ制作",
        description:
          "SNSマーケティングの基本（15分）、Instagramの役割（15分）、プロフィールの作成とカスタマイズ（90分）、Canvaを使って画像を作成（90分）、アカウントの設計を考える（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "4時間",
        icon: "book",
      },
      {
        session: "第2回【3時間】",
        title: "アルゴリズム・ストーリーズとフィード投稿",
        description:
          "アルゴリズムの理解（10分）、インサイト分析（10分）、ガイドライン（10分）、ストーリー作成（60分）、ハイライト作成（30分）、投稿テーマ設計（20分）、フィード投稿（オリジナル）作成（40分）",
        note: "",
        structure: "座学＋実習",
        duration: "3時間",
        icon: "target",
      },
      {
        session: "第3回【3時間】",
        title: "投稿運用・リール制作と総合実践",
        description:
          "投稿とキャプション・ハッシュタグ設計（40分）、リール動画作成（60分）、オリジナルコンテンツ総合制作（40分）、成功アカウント事例（20分）、未来のInstagramとトレンド（10分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "TikTok",
    items: [
      {
        session: "第1回【4時間】",
        title: "TikTok基礎・初級〜中級クリエイティブ制作",
        description:
          "TikTokの基本（10分）、プロフィール作成とカスタマイズ（30分）、動画の作成（初級編）（60分）、コンテンツの配信（10分）、コミュニティとのエンゲージメント・分析（10分）、広告とマネタイズ（10分）、ガイドライン（10分）、ケーススタディ（10分）、動画作成（中級編）（90分）",
        note: "",
        structure: "座学＋実習",
        duration: "4時間",
        icon: "book",
      },
      {
        session: "第2回【3時間】",
        title: "上級動画制作とベンチマーク・オリジナル制作",
        description:
          "動画作成（上級編）（60分）、00を真似てみよう！（60分）、オリジナルを作ってみよう！（60分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "target",
      },
      {
        session: "第3回【3時間】",
        title: "オリジナル制作ワークとトレンド活用",
        description:
          "オリジナルを作ってみよう！（50分）、完全オリジナルを作ってみよう！（80分）、成功アカウント事例分析（30分）、未来のTikTokとトレンド（10分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "X（旧Twitter）",
    items: [
      {
        session: "第1回【4時間】",
        title: "Xの基本・アカウント設計とコンテンツ戦略",
        description:
          "Xプラットフォームの特徴と最新動向（15分）、ビジネスアカウントの作成とプロフィール設計（45分）、基本的な投稿作成とフォーマット（40分）、タイムラインとアルゴリズムの理解（20分）、効果的な投稿文の書き方（30分）、ハッシュタグ・メンション活用術（20分）、画像・動画付き投稿の作成（40分）、エンゲージメントを高めるテクニック（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "4時間",
        icon: "book",
      },
      {
        session: "第2回【3時間】",
        title: "スレッド・スペース・分析と広告運用",
        description:
          "スレッド（長文投稿）の活用（30分）、Xスペース（音声配信）の運用（20分）、Xアナリティクスの見方と改善施策（30分）、ガイドラインと炎上対策（10分）、X広告の種類と出稿方法（30分）、ターゲティングと予算設定（20分）、投稿カレンダーの設計と運用計画（20分）",
        note: "",
        structure: "座学＋実習",
        duration: "3時間",
        icon: "target",
      },
      {
        session: "第3回【3時間】",
        title: "実践ワークとトレンド活用",
        description:
          "オリジナルコンテンツの企画・投稿（80分）、キャンペーン企画の立案（40分）、成功アカウント事例分析（30分）、最新トレンドと今後のX活用（20分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "LINE公式アカウント",
    items: [
      {
        session: "第1回【4時間】",
        title: "LINE公式アカウント開設・メッセージ配信とリッチメニュー",
        description:
          "LINE公式アカウントの概要と活用メリット（15分）、アカウント開設とプロフィール設定（45分）、管理画面の基本操作（20分）、あいさつメッセージ・応答メッセージの設定（20分）、メッセージの種類と配信方法（20分）、リッチメッセージ・カードタイプメッセージの作成（50分）、リッチメニューの設計と設定（50分）",
        note: "",
        structure: "座学＋実習",
        duration: "4時間",
        icon: "book",
      },
      {
        session: "第2回【3時間】",
        title: "セグメント配信・ステップ配信と分析・広告",
        description:
          "オーディエンスとセグメント配信（20分）、ステップ配信の設計と設定（40分）、クーポン・ショップカードの活用（30分）、LINE公式アカウントの分析機能（20分）、LINE広告の基本と出稿方法（20分）、友だち獲得施策の設計（20分）、実践ワーク：配信計画の作成（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "3時間",
        icon: "target",
      },
      {
        session: "第3回【3時間】",
        title: "総合実践と運用計画策定",
        description:
          "オリジナルリッチメニュー・メッセージの作成（60分）、ステップ配信シナリオの設計ワーク（50分）、総合実践ワーク（40分）、成功事例の分析（20分）、今後の運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "YouTube",
    items: [
      {
        session: "第1回【4時間】",
        title: "YouTube基礎・チャンネル設計と撮影・動画制作",
        description:
          "YouTube活用の最新動向と企業活用事例（15分）、チャンネル開設とブランディング設定（45分）、動画コンテンツの企画方法（30分）、YouTube SEOの基本（20分）、撮影の基本テクニック（構図・照明・音声）（30分）、スマートフォンでの撮影実践（40分）、著作権・ガイドラインの理解（10分）、動画素材の整理と管理（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "4時間",
        icon: "book",
      },
      {
        session: "第2回【3時間】",
        title: "動画編集・サムネイルとショート・分析",
        description:
          "動画編集ツールの基本操作（30分）、カット編集・テロップ・BGM挿入（40分）、サムネイル制作の基本とデザイン実践（30分）、YouTubeショートの企画と制作（40分）、YouTube Studioの分析機能（20分）、視聴データの読み方と改善施策（20分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "target",
      },
      {
        session: "第3回【3時間】",
        title: "オリジナル動画制作と運用計画",
        description:
          "オリジナル動画の企画・撮影・編集（90分）、コンテンツカレンダーの設計（30分）、収益化の仕組みと条件（10分）、成功チャンネル事例分析（20分）、今後のYouTube活用とトレンド（20分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "3時間",
        icon: "users",
      },
    ],
    totalHours: "10時間",
  },
];

// 4回コース（各プラットフォーム 10時間 = 2.5h × 4）
const FOUR_SESSION_CURRICULA: PlatformCurriculum[] = [
  {
    platform: "Instagram",
    items: [
      {
        session: "第1回【2.5時間】",
        title: "Instagram基礎とプロフィール設計",
        description:
          "SNSマーケティングの基本（15分）、Instagramの役割（15分）、プロフィールの作成とカスタマイズ（90分）、アカウントの設計を考える（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "book",
      },
      {
        session: "第2回【2.5時間】",
        title: "クリエイティブ制作とアルゴリズム理解",
        description:
          "Canvaを使って画像を作成（90分）、アルゴリズムの理解（10分）、インサイト分析（10分）、ガイドライン（10分）、ストーリー作成の基本（30分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
      {
        session: "第3回【2.5時間】",
        title: "ストーリーズ・フィード投稿とテーマ設計",
        description:
          "ストーリー作成実践（30分）、ハイライト作成（30分）、投稿テーマ設計（20分）、フィード投稿（オリジナル）作成（50分）、投稿とキャプション・ハッシュタグ設計（20分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "users",
      },
      {
        session: "第4回【2.5時間】",
        title: "リール制作と総合実践",
        description:
          "リール動画作成（60分）、オリジナルコンテンツ総合制作（40分）、成功アカウント事例（20分）、未来のInstagramとトレンド（20分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "TikTok",
    items: [
      {
        session: "第1回【2.5時間】",
        title: "TikTokの基本と初級クリエイティブ",
        description:
          "TikTokの基本（10分）、プロフィール作成とカスタマイズ（30分）、動画の作成（初級編）（60分）、コンテンツの配信（10分）、コミュニティとのエンゲージメント・分析（10分）、広告とマネタイズ（10分）、ガイドライン（10分）、ケーススタディ（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "book",
      },
      {
        session: "第2回【2.5時間】",
        title: "中級〜上級動画制作",
        description:
          "動画作成（中級編）（60分）、動画作成（上級編）（60分）、00を真似てみよう！（30分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
      {
        session: "第3回【2.5時間】",
        title: "ベンチマークとオリジナル制作ワーク①",
        description:
          "00を真似てみよう！（40分）、オリジナルを作ってみよう！（60分）、完全オリジナルを作ってみよう！（50分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "users",
      },
      {
        session: "第4回【2.5時間】",
        title: "オリジナル制作ワーク②とトレンド活用",
        description:
          "完全オリジナル動画の仕上げ（60分）、成功アカウント事例分析（30分）、トレンド分析と活用方法（30分）、未来のTikTokとトレンド（10分）、運用計画策定（20分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "X（旧Twitter）",
    items: [
      {
        session: "第1回【2.5時間】",
        title: "Xの基本とアカウント設計",
        description:
          "Xプラットフォームの特徴と最新動向（15分）、ビジネスアカウントの作成とプロフィール設計（45分）、基本的な投稿作成とフォーマット（40分）、タイムラインとアルゴリズムの理解（20分）、効果的な投稿文の書き方（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "book",
      },
      {
        session: "第2回【2.5時間】",
        title: "コンテンツ戦略とエンゲージメント",
        description:
          "ハッシュタグ・メンション活用術（20分）、画像・動画付き投稿の作成（40分）、エンゲージメントを高めるテクニック（30分）、スレッド（長文投稿）の活用（30分）、Xスペース（音声配信）の運用（20分）、ガイドラインと炎上対策（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "target",
      },
      {
        session: "第3回【2.5時間】",
        title: "分析・広告運用と投稿カレンダー設計",
        description:
          "Xアナリティクスの見方と改善施策（30分）、X広告の種類と出稿方法（30分）、ターゲティングと予算設定（20分）、投稿カレンダーの設計と運用計画（30分）、オリジナル投稿の作成ワーク（40分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "users",
      },
      {
        session: "第4回【2.5時間】",
        title: "実践ワークとトレンド活用",
        description:
          "オリジナルコンテンツの企画・投稿（60分）、キャンペーン企画の立案（40分）、成功アカウント事例分析（20分）、最新トレンドと今後のX活用（20分）、運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "LINE公式アカウント",
    items: [
      {
        session: "第1回【2.5時間】",
        title: "LINE公式アカウントの基本と開設",
        description:
          "LINE公式アカウントの概要と活用メリット（15分）、アカウント開設とプロフィール設定（45分）、管理画面の基本操作（30分）、あいさつメッセージ・応答メッセージの設定（30分）、メッセージの種類と配信方法（30分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "book",
      },
      {
        session: "第2回【2.5時間】",
        title: "リッチメニュー・メッセージとセグメント配信",
        description:
          "リッチメッセージ・カードタイプメッセージの作成（40分）、リッチメニューの設計と設定（40分）、オーディエンスとセグメント配信（30分）、クーポン・ショップカードの活用（30分）、ステップ配信の概要（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "target",
      },
      {
        session: "第3回【2.5時間】",
        title: "ステップ配信・分析と広告",
        description:
          "ステップ配信の設計と設定（40分）、LINE公式アカウントの分析機能（25分）、LINE広告の基本と出稿方法（25分）、友だち獲得施策の設計（20分）、実践ワーク：配信計画の作成（40分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "users",
      },
      {
        session: "第4回【2.5時間】",
        title: "総合実践と運用計画策定",
        description:
          "オリジナルリッチメニュー・メッセージの作成（50分）、ステップ配信シナリオの設計ワーク（40分）、総合実践ワーク（30分）、成功事例の分析（20分）、今後の運用計画策定（10分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
    ],
    totalHours: "10時間",
  },
  {
    platform: "YouTube",
    items: [
      {
        session: "第1回【2.5時間】",
        title: "YouTubeの基本・チャンネル設計と撮影基礎",
        description:
          "YouTube活用の最新動向と企業活用事例（15分）、チャンネル開設とブランディング設定（35分）、動画コンテンツの企画方法（30分）、YouTube SEOの基本（15分）、撮影の基本テクニック（構図・照明・音声）（25分）、著作権・ガイドラインの理解（10分）",
        note: "",
        structure: "座学＋実習",
        duration: "2.5時間",
        icon: "book",
      },
      {
        session: "第2回【2.5時間】",
        title: "撮影実践と動画編集・サムネイル",
        description:
          "スマートフォンでの撮影実践（40分）、動画素材の整理と管理（10分）、動画編集ツールの基本操作（30分）、カット編集・テロップ・BGM挿入（40分）、サムネイル制作の基本とデザイン実践（30分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
      {
        session: "第3回【2.5時間】",
        title: "YouTubeショートと分析・改善",
        description:
          "YouTubeショートの企画と制作（50分）、YouTube Studioの分析機能（30分）、視聴データの読み方と改善施策（30分）、収益化の仕組みと条件（10分）、コンテンツカレンダーの設計（30分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "users",
      },
      {
        session: "第4回【2.5時間】",
        title: "オリジナル動画制作と運用計画",
        description:
          "オリジナル動画の企画・撮影・編集（80分）、成功チャンネル事例分析（30分）、今後のYouTube活用とトレンド（20分）、運用計画策定（20分）",
        note: "",
        structure: "実習中心",
        duration: "2.5時間",
        icon: "target",
      },
    ],
    totalHours: "10時間",
  },
];

const CURRICULA_BY_SESSION: Record<SessionCount, PlatformCurriculum[]> = {
  "3": THREE_SESSION_CURRICULA,
  "4": FOUR_SESSION_CURRICULA,
  "5": FIVE_SESSION_CURRICULA,
};

export const SNS_CURRICULA: PlatformCurriculum[] =
  CURRICULA_BY_SESSION[SESSION_COUNT].length > 0
    ? CURRICULA_BY_SESSION[SESSION_COUNT]
    : FIVE_SESSION_CURRICULA; // フォールバック

export const COMPANY_INFO = {
  NAME: "Sky Grid Inc.",
  PROGRAM_NAME: "SNS運用研修プログラム",
} as const;
